import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  FormLabel,
  Link as MuiLink,
  Card,
  CardActionArea,
  CardContent,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import ArticleIcon from "@mui/icons-material/Article";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

import { Banner } from "../../../components/common/Banner";
import { PageLoader } from "../../../components/loader";
import { getMaterialsContent, getMaterialCategories } from "../../../services/materialContentService";

/* ====================== Helpers genéricos ====================== */

const targetFor = (u) => {
  const s = String(u || "");
  return s.startsWith("/") || s.toLowerCase().startsWith("mailto:")
    ? "_self"
    : "_blank";
};

const relFor = (u) => (targetFor(u) === "_blank" ? "noreferrer" : undefined);

const getDomain = (u) => {
  try {
    const { hostname } = new URL(u);
    return hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const formatDate = (d) =>
  d instanceof Date && !isNaN(+d) ? d.toLocaleDateString() : null;

const getFileName = (url) => {
  if (!url) return "Archivo";
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "Archivo";
  } catch {
    return url.split("/").pop() || "Archivo";
  }
};

/**
 * Para el destacado:
 * Trunca por nº de palabras y por nº de caracteres
 * para que se adapte mejor al ancho disponible.
 */
const truncateWords = (text, maxWords = 60, maxChars = 260) => {
  if (!text) return "";
  const s = String(text).trim();
  if (!s) return "";

  const words = s.split(/\s+/);
  let result = s;

  if (words.length > maxWords) {
    result = words.slice(0, maxWords).join(" ");
  }

  if (result.length > maxChars) {
    result = result.slice(0, maxChars);
  }

  if (result.length < s.length) {
    result = result.replace(/[\s.,;:!?\-]+$/, "");
    result += "...";
  }

  return result;
};

/**
 * Para las tarjetas inferiores:
 * solo limitamos por nº de palabras para evitar textos exagerados,
 * y dejamos que CSS (line-clamp: 2) se encargue de cortar EXACTO
 * al final de la segunda línea con "…".
 */
const truncateTwoLines = (text, maxWords = 80) => {
  if (!text) return "";
  const s = String(text).trim();
  if (!s) return "";

  const words = s.split(/\s+/);
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(" ") + "...";
  }
  return s;
};

// Obtener imagen de categoría (se usará con categorías cargadas desde backend)
const getCategoryImage = (category, categoriesList) => {
  if (!category || !categoriesList) return null;
  const cat = categoriesList.find((c) => c.key === category);
  return cat?.imageUrl || null;
};

// Obtener label de categoría desde su key
const getCategoryLabel = (categoryKey, categoriesList) => {
  if (!categoryKey || !categoriesList) return categoryKey || "";
  const cat = categoriesList.find((c) => c.key === categoryKey);
  return cat?.label || categoryKey;
};

/** ====================== Página Materiales ====================== */
export default function Materials() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);

  // Filtros
  const [q, setQ] = useState("");
  const [order, setOrder] = useState("desc");
  const [filterYear, setFilterYear] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [detailModal, setDetailModal] = useState(null);

  // === Breakpoints ===
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("md"));

  // Aproximación de caracteres por línea según el ancho (solo para destacado)
  const charsPerLine = isXs ? 45 : isMd ? 75 : 95;
  const featuredMaxChars = charsPerLine * 3; // ~3 líneas en destacado

  async function loadCategories() {
    try {
      const cats = await getMaterialCategories().catch(() => []);
      setCategories(cats || []);
    } catch (e) {
      console.error("Error cargando categorías:", e);
    }
  }

  async function loadContent() {
    try {
      setLoading(true);
      setError(null);
      const data = await getMaterialsContent();
      const list = Array.isArray(data?.sections) ? data.sections : [];
      const normalized = list.map((s) => {
        const created = s?.createdAt ? new Date(s.createdAt) : null;
        const updated = s?.updatedAt ? new Date(s.updatedAt) : null;
        const date = updated || created;
        return {
          title: String(s?.title || "").trim(),
          description: s?.description ? String(s.description).trim() : "",
          url: s?.url ? String(s.url).trim() : "",
          imageUrl: s?.imageUrl ? String(s.imageUrl).trim() : "",
          fileUrl: s?.fileUrl ? String(s.fileUrl).trim() : "",
          filePublicId: s?.filePublicId || null,
          createdAt: created,
          updatedAt: updated,
          year: date ? date.getFullYear() : null,
          category: s?.category ? String(s.category).trim() : "",
        };
      });
      setSections(normalized);
    } catch (e) {
      const msg = Array.isArray(e?.data?.message)
        ? e.data.message.join(", ")
        : e?.message || "Hubo un error al cargar el material";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadContent();
    loadCategories();
  }, []);

  // Opciones filtros
  const yearOptions = useMemo(() => {
    const years = new Set(sections.map((s) => s.year).filter(Boolean));
    return [""].concat([...years].sort((a, b) => b - a).map(String));
  }, [sections]);

  const categoryOptions = useMemo(() => {
    const cats = new Set(sections.map((s) => s.category).filter(Boolean));
    return [""].concat([...cats].sort());
  }, [sections]);

  const filtered = useMemo(() => {
    let base = sections;

    const term = q.trim().toLowerCase();
    if (term) {
      base = base.filter(
        (it) =>
          it.title.toLowerCase().includes(term) ||
          it.description.toLowerCase().includes(term) ||
          it.url.toLowerCase().includes(term)
      );
    }

    if (filterYear) {
      base = base.filter((it) => String(it.year) === filterYear);
    }

    if (filterCategory) {
      base = base.filter((it) => it.category === filterCategory);
    }

    const arr = [...base];
    arr.sort((a, b) => {
      const ta = +(a.updatedAt || a.createdAt || 0);
      const tb = +(b.updatedAt || b.createdAt || 0);
      return order === "desc" || !order ? tb - ta : ta - tb;
    });

    return arr;
  }, [sections, q, order, filterYear, filterCategory]);

  const featured =
    filtered.length > 0 && (order === "desc" || !order) ? filtered[0] : null;
  const listItems = featured ? filtered.slice(1) : filtered;

  if (loading) return <PageLoader message="Cargando material…" />;

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
      >
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner
        title="Material de Interés"
        message="Publicaciones y recursos de interés."
        className="mb-6"
      />

      <Paper elevation={2} sx={{ borderRadius: 2, minWidth: 0, overflow: "hidden" }}>
        {/* ========= Filtros ========= */}
        <Box sx={{ p: 3, borderBottom: "1px solid #eee", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#0488AF",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <ArticleIcon fontSize="small" />
            Filtros y Acciones
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "flex-end" },
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <TextField
              size="small"
              label="Buscar (título, descripción o vínculo)"
              placeholder="Ej: sostenibilidad"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 280px" },
                minWidth: 280,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 200, width: 200 }}>
              <FormLabel
                sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
              >
                Orden
              </FormLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  px: 1,
                  height: 40,
                }}
              >
                <IconButton size="small" disableRipple>
                  <SortIcon fontSize="small" />
                </IconButton>
                <Select
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  fullWidth
                  variant="standard"
                  disableUnderline
                  sx={{ ml: 1 }}
                >
                  <MenuItem value="desc">Recientes primero</MenuItem>
                  <MenuItem value="asc">Antiguos primero</MenuItem>
                </Select>
              </Box>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, width: 200 }}>
              <FormLabel
                sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
              >
                Año
              </FormLabel>
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, height: 40 }}
              >
                <MenuItem value="">Todos los años</MenuItem>
                {yearOptions.slice(1).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {categoryOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200, width: 200 }}>
                <FormLabel
                  sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                >
                  Categoría
                </FormLabel>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2, height: 40 }}
                >
                  <MenuItem value="">Todas las categorías</MenuItem>
                  {categoryOptions.slice(1).map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {getCategoryLabel(cat, categories)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* ========= Contenido ========= */}
        <Box sx={{ p: 3, minWidth: 0 }}>
          {filtered.length === 0 ? (
            <Typography color="text.secondary">
              Aún no hay materiales publicados.
            </Typography>
          ) : (
            <>
              {/* --- Lista de materiales --- */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                {/* Material más reciente */}
                {featured && (
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label="MÁS RECIENTE"
                      size="small"
                      sx={{
                        bgcolor: "#0488AF",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        height: 24,
                      }}
                    />
                  </Box>
                )}
                {featured && (
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      border: "2px solid #0488AF",
                      transition: "all 0.2s",
                      cursor: "pointer",
                      minWidth: 0,
                      "&:hover": {
                        boxShadow: 2,
                        borderColor: "#0488AF",
                      },
                    }}
                    onClick={() => setDetailModal(featured)}
                  >
                    <CardActionArea
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "stretch",
                        minWidth: 0,
                      }}
                    >
                      {featured.imageUrl ||
                        (featured.category &&
                          getCategoryImage(featured.category, categories)) ? (
                        <Box
                          component="img"
                          src={
                            featured.imageUrl ||
                            getCategoryImage(featured.category, categories)
                          }
                          alt={featured.title || "Material"}
                          loading="lazy"
                          onError={(ev) => {
                            ev.currentTarget.src = "";
                            ev.currentTarget.style.display = "none";
                          }}
                          sx={{
                            width: { xs: "100%", sm: 200 },
                            height: { xs: 200, sm: 200 },
                            minWidth: { sm: 200 },
                            maxWidth: { sm: 200 },
                            objectFit: "cover",
                            objectPosition: "center",
                            bgcolor: "#f3f4f6",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: { xs: "100%", sm: 200 },
                            height: { xs: 200, sm: 200 },
                            minWidth: { sm: 200 },
                            maxWidth: { sm: 200 },
                            bgcolor: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.disabled",
                            flexShrink: 0,
                          }}
                        >
                          <ImageIcon />
                        </Box>
                      )}

                      <CardContent
                        sx={{
                          flex: 1,
                          p: 2.5,
                          minWidth: 0,
                          maxWidth: { sm: "calc(100% - 200px)" },
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            mb: 1,
                            flexWrap: "wrap",
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              flex: 1,
                              color: "#0488AF",
                              overflowWrap: "break-word",
                              maxWidth: "100%",
                              minWidth: 0,
                            }}
                          >
                            {featured.title || "Material"}
                          </Typography>

                          {formatDate(featured.updatedAt || featured.createdAt) && (
                            <Chip
                              size="small"
                              label={formatDate(featured.updatedAt || featured.createdAt)}
                              sx={{
                                bgcolor: "#eef6fb",
                                color: "#0488AF",
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {featured.category && (
                            <Chip
                              size="small"
                              label={getCategoryLabel(featured.category, categories)}
                              sx={{
                                bgcolor: "#f0f9ff",
                                color: "#0369a1",
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>

                        {featured.description ? (
                          <Box
                            component="div"
                            sx={{
                              mb: 1.5,
                              fontSize: "0.875rem",
                              lineHeight: 1.5,
                              color: "text.secondary",
                              height: "3rem", // ~2 líneas
                              maxWidth: "100%",
                              width: "100%",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2, // 2 líneas exactas
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {truncateTwoLines(featured.description, 80)}
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1.5, height: "3rem" }} />
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          {featured.url && (
                            <Box
                              component={MuiLink}
                              href={featured.url}
                              target={targetFor(featured.url)}
                              rel={relFor(featured.url)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "#0488AF",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textDecoration: "none",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              <LinkIcon fontSize="small" />
                              <span>
                                Fuente
                                {getDomain(featured.url)
                                  ? ` · ${getDomain(featured.url)}`
                                  : ""}
                              </span>
                            </Box>
                          )}

                          {featured.fileUrl && (
                            <Box
                              component="a"
                              href={featured.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                color: "#0488AF",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textDecoration: "none",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              <AttachFileIcon fontSize="small" />
                              <span>{getFileName(featured.fileUrl)}</span>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                )}

                {/* Resto de materiales */}
                {listItems.map((s, i) => {
                    const date = formatDate(s.updatedAt || s.createdAt);
                    const domain = s.url ? getDomain(s.url) : "";

                    return (
                      <Card
                        key={`${s.title}-${i}`}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          transition: "all 0.2s",
                          cursor: "pointer",
                          minWidth: 0,
                          "&:hover": {
                            boxShadow: 2,
                            borderColor: "#0488AF",
                          },
                        }}
                        onClick={() => setDetailModal(s)}
                      >
                        <CardActionArea
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "stretch",
                            minWidth: 0,
                          }}
                        >
                          {s.imageUrl ||
                            (s.category && getCategoryImage(s.category, categories)) ? (
                              <Box
                                component="img"
                                src={s.imageUrl || getCategoryImage(s.category, categories)}
                              alt={s.title || "Material"}
                              loading="lazy"
                              onError={(ev) => {
                                ev.currentTarget.src = "";
                                ev.currentTarget.style.display = "none";
                              }}
                              sx={{
                                width: { xs: "100%", sm: 200 },
                                height: { xs: 200, sm: 200 },
                                minWidth: { sm: 200 },
                                maxWidth: { sm: 200 },
                                objectFit: "cover",
                                objectPosition: "center",
                                bgcolor: "#f3f4f6",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: { xs: "100%", sm: 200 },
                                height: { xs: 200, sm: 200 },
                                minWidth: { sm: 200 },
                                maxWidth: { sm: 200 },
                                bgcolor: "#f3f4f6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.disabled",
                                flexShrink: 0,
                              }}
                            >
                              <ImageIcon />
                            </Box>
                          )}

                          <CardContent
                            sx={{
                              flex: 1,
                              p: 2.5,
                              minWidth: 0,
                              maxWidth: { sm: "calc(100% - 200px)" },
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                mb: 1,
                                flexWrap: "wrap",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  flex: 1,
                                  color: "#0488AF",
                                  overflowWrap: "break-word",
                                  maxWidth: "100%",
                                  minWidth: 0,
                                }}
                              >
                                {s.title || "Material"}
                              </Typography>

                              {date && (
                                <Chip
                                  size="small"
                                  label={date}
                                  sx={{
                                    bgcolor: "#eef6fb",
                                    color: "#0488AF",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              {s.category && (
                                <Chip
                                  size="small"
                                  label={getCategoryLabel(s.category, categories)}
                                  sx={{
                                    bgcolor: "#f0f9ff",
                                    color: "#0369a1",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>

                            {s.description ? (
                              <Box
                                component="div"
                                sx={{
                                  mb: 1.5,
                                  fontSize: "0.875rem",
                                  lineHeight: 1.5,
                                  color: "text.secondary",
                                  height: "3rem", // ~2 líneas
                                  maxWidth: "100%",
                                  width: "100%",
                                  minWidth: 0,
                                  overflow: "hidden",
                                }}
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2, // 2 líneas exactas
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {truncateTwoLines(s.description, 80)}
                              </Box>
                            ) : (
                              <Box sx={{ mb: 1.5, height: "3rem" }} />
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              {s.url && (
                                <Box
                                  component={MuiLink}
                                  href={s.url}
                                  target={targetFor(s.url)}
                                  rel={relFor(s.url)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: "#0488AF",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    textDecoration: "none",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  <LinkIcon fontSize="small" />
                                  <span>
                                    Fuente
                                    {domain ? ` · ${domain}` : ""}
                                  </span>
                                </Box>
                              )}

                              {s.fileUrl && (
                                <Box
                                  component="a"
                                  href={s.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    color: "#0488AF",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    textDecoration: "none",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  <AttachFileIcon />
                                  <span>{getFileName(s.fileUrl)}</span>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* ========= Modal de detalles ========= */}
      {detailModal && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          onClick={() => setDetailModal(null)}
        >
          <Paper
            sx={{
              maxWidth: 800,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              borderRadius: 3,
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                background: "linear-gradient(135deg, #0b3a66 0%, #0b6b93 50%, #0488AF 100%)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              {/* Efectos de fondo */}
              <Box
                sx={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  filter: "blur(40px)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  left: -30,
                  width: 150,
                  height: 150,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                  filter: "blur(30px)",
                }}
              />
              
              <Box
                sx={{
                  position: "relative",
                  p: 3,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <ArticleIcon 
                      sx={{ 
                        color: "rgba(255, 255, 255, 0.9)", 
                        fontSize: "1.75rem" 
                      }} 
                    />
                    <Typography
                      variant="h5"
                      sx={{ 
                        fontWeight: 700, 
                        color: "white", 
                        flex: 1,
                        minWidth: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      {detailModal.title || "Material"}
                    </Typography>
                    {detailModal.year && (
                      <Chip
                        size="small"
                        label={detailModal.year}
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          color: "white",
                          fontWeight: 600,
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                        }}
                      />
                    )}
                  </Box>

                  {formatDate(detailModal.updatedAt || detailModal.createdAt) && (
                    <Typography
                      variant="caption"
                      sx={{ 
                        fontSize: "0.8rem",
                        color: "rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {formatDate(detailModal.updatedAt || detailModal.createdAt)}
                    </Typography>
                  )}
                </Box>

                <IconButton
                  onClick={() => setDetailModal(null)}
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    "&:hover": { 
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {(detailModal.imageUrl ||
                (detailModal.category &&
                  getCategoryImage(detailModal.category, categories))) && (
                <Box
                  component="img"
                  src={
                    detailModal.imageUrl ||
                    getCategoryImage(detailModal.category, categories)
                  }
                  alt={detailModal.title || "Material"}
                  loading="lazy"
                  onError={(ev) => {
                    ev.currentTarget.src = "";
                    ev.currentTarget.style.display = "none";
                  }}
                  sx={{
                    width: "100%",
                    maxHeight: 300,
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: "#f3f4f6",
                  }}
                />
              )}

              {detailModal.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: "#0488AF",
                    }}
                  >
                    Descripción
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}
                  >
                    {detailModal.description}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {detailModal.url && (
                  <Box
                    component={MuiLink}
                    href={detailModal.url}
                    target={targetFor(detailModal.url)}
                    rel={relFor(detailModal.url)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      bgcolor: "#f8f9fa",
                      textDecoration: "none",
                      color: "#0488AF",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "#eef6fb",
                        borderColor: "#0488AF",
                      },
                    }}
                  >
                    <LinkIcon />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 0.5, color: "#0488AF" }}
                      >
                        Ver fuente
                      </Typography>
                      {getDomain(detailModal.url) ? (
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: "#0488AF",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {getDomain(detailModal.url)}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: "#0488AF",
                            fontWeight: 500,
                            wordBreak: "break-all",
                          }}
                        >
                          {detailModal.url}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {detailModal.fileUrl && (
                  <Box
                    component="a"
                    href={detailModal.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      bgcolor: "#f8f9fa",
                      textDecoration: "none",
                      color: "#0488AF",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "#eef6fb",
                        borderColor: "#0488AF",
                      },
                    }}
                  >
                    <AttachFileIcon />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        Descargar archivo adjunto
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {getFileName(detailModal.fileUrl)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </div>
  );
}
